/**
 * PIG-595 — family-aware microphone display names.
 *
 * The ceiling (`tcm_x`) and table (`ttm_x`) Parlé microphones ship under the Devio
 * (Tree Frog / Android) product family as the DCM-X / DTM-X twins. The `model` key
 * is the stable identity; only the display name (`modelName`, shown in the
 * Microphones menu, the Microphone-locations tab, and on placed devices) changes
 * with the selected family. Deriving the name purely from `(model, productFamily)`
 * keeps every surface consistent with the equipment list — which already resolves
 * DCM-X from the family — and is reversible when the family changes back.
 */
export const DEVIO_MIC_DISPLAY_NAME: Record<string, string> = {
  tcm_x: 'Parlé DCM-X',
  ttm_x: 'Parlé DTM-X',
};

export const PARLE_MIC_DISPLAY_NAME: Record<string, string> = {
  tcm_x: 'Parlé TCM-X',
  ttm_x: 'Parlé TTM-X',
};

/** Models whose display name depends on the product family (ceiling + table). */
export const hasFamilyMicName = (model?: string): boolean =>
  Boolean(model) && model! in DEVIO_MIC_DISPLAY_NAME;

/**
 * Resolve the display name for a microphone `model`.
 * Devio UCX (Tree Frog) → the DCM-X / DTM-X twin; anything else → the Parlé name.
 * Models with no family twin (bars, pendant, ceiling-tile) return `fallback` unchanged.
 *
 * NOTE: the caller decides whether this is a Devio UCX design via `isDevioUcx` — do
 * NOT pass `productFamily === 'Devio'`, because Devio SCX (a Devio-family DSP on
 * Windows) uses the Parlé names. Use `isDevioUcxDesign(area)` from
 * `utilities/treeFrogBundleMatching`.
 *
 * @param model      product-config key, e.g. 'tcm_x'
 * @param isDevioUcx true only for a genuine Devio UCX (Tree Frog / Android) design
 * @param fallback   name to use when the model has no family twin
 */
export const familyMicModelName = (
  model: string | undefined,
  isDevioUcx: boolean,
  fallback?: string,
): string | undefined => {
  if (!model) return fallback;
  const map = isDevioUcx ? DEVIO_MIC_DISPLAY_NAME : PARLE_MIC_DISPLAY_NAME;
  return map[model] ?? fallback;
};
